/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';


export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: CodeBlock[] = [];
    const project: ProjectConfiguration = feature.project;
    const VARNAME = `${feature.getSettingValue('VARNAME')}`;
    const DATATYPE = feature.getSettingValue('DATATYPE');
    const FEATURE = feature.getSettingValue('FEATURE');
    const POLL_INTERVAL = feature.getSettingValue('POLL_INTERVAL');
    const linkedFeature = project.getFeature(FEATURE);

    let defaultValue = feature.getSettingValue('DEFAULT');
    if (DATATYPE === 'string') {
        defaultValue = `"${defaultValue}"`;
    } else if (defaultValue === '') {
        defaultValue = '0';
    }

    let typeCastingType;
    switch (DATATYPE) {
        case TibboDataType.DATETIME:
        case TibboDataType.TIME:
            typeCastingType = TibboDataType.INTEGER;
            break;
        default:
            typeCastingType = DATATYPE;
            break;
    }

    blocks.push({
        location: 'VARIABLE_DECLARATIONS',
        blockType: BlockType.CODE,
        id: `declare_var_${VARNAME}`,
        template: `${VARNAME}=${defaultValue}`,
    });

    // optimization for declaring changed handler
    let hasChangedHandler = false;
    for (let i = 0; i < project.features.length; i++) {
        const tmpFeature = project.features[i];
        let linkedFeatureName = '';
        if (tmpFeature.id === 'node_variable_changed') {
            if (tmpFeature.getSettingValue('VARIABLE') === feature.getID()) {
                hasChangedHandler = true;
                break;
            }
        } else if (tmpFeature.id === 'node_timer_completed') {
            const timer = project.getFeature(tmpFeature.getSettingValue('TIMER'));
            if (timer !== undefined) {
                linkedFeatureName = timer.getSettingValue('TIMER_NAME');
            }
        }
        if (linkedFeatureName !== '') {
            if (linkedFeatureName === VARNAME) {
                hasChangedHandler = true;
                break;
            }
        }
        // if cgg or web dashboard, check if variable is used in any of the widgets
        if (['cgg', 'web_dashboard'].includes(tmpFeature.id)) {
            const widgets = tmpFeature.getSettingValue('WIDGETS');
            for (let j = 0; j < widgets.length; j++) {
                if (widgets[j].getSettingValue('VARIABLE') === feature.getID()) {
                    hasChangedHandler = true;
                    break;
                }
            }
        }
    }

    let isByRef = '';
    if (DATATYPE === TibboDataType.STRING) {
        isByRef = 'byref ';
    }

    blocks.push({
        location: 'FUNCTION_DECLARATIONS',
        id: `var_${VARNAME}_update`,
        blockType: BlockType.LOCATION,
        template: `
def var_${VARNAME}_update():
    #****var_${VARNAME}_update****#
    pass`,
    });

    const MIN = feature.getSettingValue('MINIMUM');
    const MAX = feature.getSettingValue('MAXIMUM');
    let minCode = '';
    let maxCode = '';
    if (MIN !== '') {
        if (DATATYPE !== TibboDataType.STRING) {
            minCode = `
    if value < ${MIN}:
        value = ${MIN}`;
        }
    }
    if (MAX !== '') {
        if (DATATYPE !== TibboDataType.STRING) {
            maxCode = `
    if value > ${MAX}:
        value = ${MAX}`;
        }
    }

    blocks.push({
        location: 'FUNCTION_DECLARATIONS',
        id: `var_${VARNAME}_update_completed`,
        blockType: BlockType.LOCATION,
        template: `
def var_${VARNAME}_update_completed(value):
    global ${VARNAME}
    value_old = ${VARNAME}
    #****var_${VARNAME}_update_completed****#
    ${minCode}
    ${maxCode}
    ${VARNAME}=value
    if value_old != ${VARNAME}:
        ${hasChangedHandler ? `var_${VARNAME}_changed(value_old)` : ''}
        pass
    #****var_${VARNAME}_updated****#`,
    });

    let var_set_inner = '';
    if (linkedFeature && linkedFeature.id === 'stg_setting') {
        var_set_inner = `stg_set("${VARNAME}", ${project.convertDataType('value', typeCastingType, TibboDataType.STRING)})`;
    }

    blocks.push({
        location: 'FUNCTION_DECLARATIONS',
        id: `var_${VARNAME}_set`,
        blockType: BlockType.LOCATION,
        template: `
def var_${VARNAME}_set(value):
    value = ${project.convertDataType('value', TibboDataType.STRING, typeCastingType)}
    ${minCode}
    ${maxCode}
    global ${VARNAME}
    value_old = ${VARNAME}
    ${VARNAME}=value
    ${var_set_inner}
    #****var_${VARNAME}_set****#
    
    if value_old != ${VARNAME}:
        
        ${hasChangedHandler ? `var_${VARNAME}_changed(value_old)` : `
        #****var_${VARNAME}_changed****#`}
        pass
    pass
            `,
    });

    if (hasChangedHandler) {
        blocks.push({
            location: 'FUNCTION_DECLARATIONS',
            id: `var_${VARNAME}_changed`,
            blockType: BlockType.LOCATION,
            template: `
def var_${VARNAME}_changed(value_old):
    #****var_${VARNAME}_changed****#
    pass`,
        });
    }

    //
    // if ((linkedFeature && linkedFeature.id !== 'stg_setting') || !linkedFeature) {
    //     blocks.push({
    //         location: 'boot_complete',
    //         blockType: BlockType.CODE,
    //         template: `
    //     var_${VARNAME}_update_completed(${defaultValue})`,
    //     });
    // }

    // blocks.push({
    //     location: 'var_poll_timer_elapsed',
    //     blockType: BlockType.CODE,
    //     template: `var_${VARNAME}_update()`,
    // });

    if (POLL_INTERVAL === '') {
        blocks.push({
            location: 'var_poll_timer_elapsed',
            blockType: BlockType.CODE,
            template: `#****var_poll_timer_${VARNAME}_elapsed****#`,
        });
    } else {
        const TIMER_NAME = `var_poll_timer_${VARNAME}`;
        const INTERVAL = Number(POLL_INTERVAL) * 1000;
        
        blocks.push({
            location: 'FUNCTION_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
def var_poll_timer_${VARNAME}():
    while True:
        try:
            #****var_poll_timer_${VARNAME}_elapsed****#
            pass
        except Exception as e:
            
            pass
        await asyncio.sleep_ms(${INTERVAL})`,
        });
    
        blocks.push({
            location: 'ASYNC_TASKS',
            blockType: BlockType.CODE,
            template: `
var_loop = asyncio.get_event_loop()
var_loop.create_task(var_poll_timer_${VARNAME}())`,
        });
    }

    blocks.push({
        location: 'callback_luis_read',
        blockType: BlockType.CODE,
        template: `
if name="${VARNAME}" then
    value=${project.convertDataType(VARNAME, DATATYPE, TibboDataType.STRING)}
    callback_luis_read=true
end if`,
    });

    blocks.push({
        location: 'callback_luis_write',
        blockType: BlockType.CODE,
        template: `
if name="${VARNAME}" then
    ${VARNAME}=${project.convertDataType('value', TibboDataType.STRING, DATATYPE)}
    callback_luis_write=true
end if`,
    });

    return blocks;
};
